import React, { type SyntheticEvent, useEffect, useState, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import RefreshIcon from '@atlaskit/icon/glyph/refresh';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Popup from '@atlaskit/popup';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { Button, Select, colors } from '@atlassian/jira-portfolio-3-common';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import { ReportStatus } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/state/ui/main/tabs/dependencies/report-state/types';
import { getBody } from '@atlassian/jira-portfolio-3-portfolio/src/common/dom';
import { ISSUE_LINK_DIRECTION } from '@atlassian/jira-portfolio-3-portfolio/src/common/view/constant';
import commonMessages from '@atlassian/jira-portfolio-3-portfolio/src/common/view/messages';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import commonDependencyMessages from '../messages';
import IssueSelector from './issue-selector';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props, TypeOption, ContentProps } from './types';
import { handleLinkTypes, isSourceAndTargetFilteredOut } from './utils';

export const AddDependencyContent = ({
	hideAddDependency,
	issues,
	issueTypes,
	projectsById,
	addDependency,
	issueLinkTypes,
	onDependencyAdded,
	fromIssueId,
	dependencyFilters,
	filteredIssues,
	showWarningForVR,
}: ContentProps) => {
	const { formatMessage } = useIntl();
	const getFromIssue = useCallback(
		() => issues.find((issue) => issue.id === fromIssueId),
		[fromIssueId, issues],
	);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [source, setSource] = useState<any>(getFromIssue());
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const [target, setTarget] = useState<any>(null);

	const typeOptions = handleLinkTypes(issueLinkTypes);
	const [type, setType] = useState<TypeOption>(typeOptions[0]);

	useEffect(() => {
		setSource(getFromIssue());
	}, [fromIssueId, getFromIssue]);

	const isValid = !!source && !!target && !!type;

	const triggerAnalytics = (analyticsEvent: UIAnalyticsEvent, analyticsAction: string) => {
		const [actionSubject, action] = analyticsAction.split(' ');
		const analyticsPayload = {
			// differentiating whether the action is performed from an issue card or from the global add
			source: fromIssueId ? 'fromIssueAddDependencyDialog' : 'globalAddDependencyDialog',
		};

		fireUIAnalytics(analyticsEvent.update({ action, actionSubject }), analyticsPayload);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onAddDependencyConfirm = (_: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => {
		if (!isValid) {
			throw new Error('Target issue is not selected!');
		}
		const isOutward = type.direction === ISSUE_LINK_DIRECTION.OUTWARD;
		addDependency({
			sourceItemKey: isOutward ? source.id : target.id,
			targetItemKey: isOutward ? target.id : source.id,
			type: Number(type.id),
		});
		hideAddDependency();

		const issueId = isOutward ? source.id : target.id;
		onDependencyAdded(issueId);

		triggerAnalytics(analyticsEvent, PRODUCT_ANALYTICS_EVENT_NAMES.ADDED_ISSUE_DEPENDENCY_LINK);
	};

	const onCancel = () => {
		hideAddDependency();
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const swapSourceTarget = (_: SyntheticEvent<any>, analyticsEvent: UIAnalyticsEvent) => {
		setSource(target);
		setTarget(source);

		triggerAnalytics(analyticsEvent, PRODUCT_ANALYTICS_EVENT_NAMES.SWAPPED_ISSUE_DEPENDENCY_LINK);
	};

	const isIssuesFilteredOut = isSourceAndTargetFilteredOut(dependencyFilters, filteredIssues, [
		source,
		target,
	]);

	return (
		<div
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className={styles.container}
			role="dialog"
			aria-modal="false"
			aria-label={formatMessage(messages.addDependencyDialogAriaLabelText)}
		>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<div className={styles.content}>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={styles.swap}>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={`${styles.swapLine} ${styles.swapLineTop}`} />
					<Button
						testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.add-dependency.swap-direction"
						isDisabled={!source && !target}
						onClick={swapSourceTarget}
						iconBefore={<RefreshIcon label="Switch issues" size="small" />}
					/>
					{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
					<div className={`${styles.swapLine} ${styles.swapLineBottom}`} />
				</div>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={styles.form}>
					<IssueSelector
						otherIssue={target}
						{...{ issues, issueTypes, projectsById }}
						onChangeDependencyIssue={setSource}
						value={source}
						testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.add-dependency.source-issue-selector"
						ariaLabel={formatMessage(messages.selectSourceIssue)}
					/>
					<div data-testid="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.add-dependency.link-type">
						<Select
							// eslint-disable-next-line @typescript-eslint/no-shadow
							onChange={(type) => {
								// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								setType(type as TypeOption);
							}}
							isTransparentBackground={false}
							value={type}
							options={typeOptions}
							menuPortalTarget={getBody()}
							styles={{
								// eslint-disable-next-line @typescript-eslint/no-shadow
								menuPortal: (styles) => ({
									...styles,
									zIndex: 1000,
								}),
							}}
							aria-label={formatMessage(messages.selectDependencyType)}
						/>
					</div>
					<IssueSelector
						otherIssue={source}
						{...{ issues, issueTypes, projectsById }}
						onChangeDependencyIssue={setTarget}
						value={target}
						testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.add-dependency.target-issue-selector"
						ariaLabel={formatMessage(messages.selectTargetIssue)}
					/>
				</div>
			</div>
			{/* showWarningForVR is for VR test only */}
			{(showWarningForVR || isIssuesFilteredOut) && (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<div className={styles.warning}>
					<WarningIcon label="" primaryColor={token('color.icon.warning', colors.Y300)} />{' '}
					{formatMessage(messages.filteredOutWarning)}
				</div>
			)}
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
			<div className={styles.actions}>
				<Button
					testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.add-dependency.cancel-button"
					onClick={onCancel}
				>
					{formatMessage(commonMessages.cancel)}
				</Button>
				<Button
					testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.add-dependency.confirm-button"
					onClick={onAddDependencyConfirm}
					appearance="primary"
					isDisabled={!isValid}
				>
					{formatMessage(commonMessages.add)}
				</Button>
			</div>
		</div>
	);
};

const AddDependency = ({
	show,
	reportStatus,
	fromIssueId,
	showAddDependency,
	hideAddDependency,
	issues,
	issueTypes,
	projectsById,
	addDependency,
	issueLinkTypes,
	onDependencyAdded,
	isEditMode,
	dependencyFilters,
	filteredIssues,
}: Props) => {
	const { formatMessage } = useIntl();
	return isEditMode ? (
		<Popup
			placement="bottom-end"
			isOpen={show}
			onClose={hideAddDependency}
			content={() =>
				!!issues && issueTypes ? (
					<AddDependencyContent
						{...{
							show,
							hideAddDependency,
							issues,
							issueTypes,
							projectsById,
							addDependency,
							issueLinkTypes,
							onDependencyAdded,
							fromIssueId,
							dependencyFilters,
							filteredIssues,
						}}
					/>
				) : null
			}
			trigger={(triggerProps) => (
				<div {...triggerProps}>
					<Button
						testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.add-dependency.add-button"
						appearance="default"
						isSelected={show}
						onClick={show ? hideAddDependency : showAddDependency}
						iconBefore={<AddIcon label="" />}
						isDisabled={reportStatus !== ReportStatus.SUCCESS}
						aria-haspopup="dialog"
					>
						{formatMessage(commonDependencyMessages.addDependency)}
					</Button>
				</div>
			)}
		/>
	) : null;
};

export default AddDependency;
